import React, { useState, useEffect } from 'react';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, Input } from 'reactstrap';
import { FaExchangeAlt, FaPlus } from 'react-icons/fa';

// Super-admin only. Lets a super-admin switch the active customer for their session so they can
// diagnose any customer's data. Switching re-issues the auth cookie server-side, so we hard-reload
// the page afterwards to refetch everything under the new customer context.
export const CustomerSwitcher = () => {
    const [customers, setCustomers] = useState([]);
    const [current, setCurrent] = useState('');
    const [home, setHome] = useState('');
    const [switching, setSwitching] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetch('api/identity/admin/customers');
                if (response.ok) {
                    const data = await response.json();
                    setCustomers(data.customers || []);
                    setCurrent(data.current || '');
                    setHome(data.home || '');
                }
            } catch {
                // non-fatal: switcher just won't populate
            }
        };
        load();
    }, []);

    const switchTo = async (customer) => {
        if (switching || customer === current) return;
        setSwitching(true);
        try {
            const response = await fetch('api/identity/switch-customer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ customer })
            });
            if (response.ok) {
                window.location.reload();
            } else {
                const text = await response.text();
                alert(text || 'Failed to switch customer.');
                setSwitching(false);
            }
        } catch {
            alert('Failed to switch customer.');
            setSwitching(false);
        }
    };

    const openCreate = () => {
        setNewName('');
        setCreateError('');
        setCreateOpen(true);
    };

    const createCustomer = async (e) => {
        if (e) e.preventDefault();
        const name = newName.trim();
        if (!name) {
            setCreateError('Please enter a customer name.');
            return;
        }
        setCreating(true);
        setCreateError('');
        try {
            const response = await fetch('api/identity/admin/create-customer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ customer: name })
            });
            if (response.ok) {
                // The session is already switched into the new customer server-side; reload everything.
                window.location.reload();
            } else {
                const text = await response.text();
                setCreateError(text || 'Failed to create customer.');
                setCreating(false);
            }
        } catch {
            setCreateError('Failed to create customer.');
            setCreating(false);
        }
    };

    const awayFromHome = home && current && current !== home;

    return (
        <UncontrolledDropdown nav inNavbar>
            <DropdownToggle nav caret>
                <FaExchangeAlt /> {current}{awayFromHome ? ' *' : ''}
            </DropdownToggle>
            <DropdownMenu end style={{ maxHeight: 400, overflowY: 'auto' }}>
                {awayFromHome && (
                    <>
                        <DropdownItem onClick={() => switchTo(home)}>
                            Return to home ({home})
                        </DropdownItem>
                        <DropdownItem divider />
                    </>
                )}
                {customers.map(c => (
                    <DropdownItem key={c} active={c === current} onClick={() => switchTo(c)}>
                        {c}{c === home ? ' (home)' : ''}
                    </DropdownItem>
                ))}
                <DropdownItem divider />
                <DropdownItem onClick={openCreate}>
                    <FaPlus /> Create new customer…
                </DropdownItem>
            </DropdownMenu>

            <Modal isOpen={createOpen} toggle={() => !creating && setCreateOpen(false)}>
                <ModalHeader toggle={() => !creating && setCreateOpen(false)}>Create new customer</ModalHeader>
                <Form onSubmit={createCustomer}>
                    <ModalBody>
                        {createError && <div className="alert alert-danger">{createError}</div>}
                        <p>Creates an empty customer and switches you into it so you can set it up.</p>
                        <Input
                            type="text"
                            placeholder="Customer name"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            autoFocus
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit" disabled={creating || !newName.trim()}>
                            {creating ? 'Creating…' : 'Create'}
                        </Button>
                        <Button color="secondary" type="button" onClick={() => setCreateOpen(false)} disabled={creating}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </UncontrolledDropdown>
    );
};
